<template>
  <div class="child-route-wrapper">
    <h1 class="registration-form-title">
      Create an account
    </h1>
    <p class="registration-form-subtitle">
      Sign up to create an account and explore many things
    </p>
    <validation-observer
      #default="{ invalid }"
      class="registration-form-wrapper"
    >
      <b-form
        class="registration-form"
        @submit.prevent="onSubmit"
      >
        <validation-provider
          v-slot="{ errors, touched, valid }"
          name="email"
          vid="email"
          rules="required|email"
        >
          <b-form-group
            class="registration-input-group"
            label="Email address"
            label-for="email"
            label-class="registration-label"
          >
            <b-form-input
              id="email"
              v-model="email"
              class="form-control"
              type="email"
              placeholder="Your account email"
              :state="errors.length > 0 && touched ? false : null"
              :class="{ 'is-valid': valid && touched }"
            />
            <div
              v-if="errors.length > 0 && touched"
              class="invalid-feedback d-block input-error"
            >
              {{ errors[0] }}
            </div>
          </b-form-group>
        </validation-provider>

        <validation-provider
          v-slot="{ errors, touched, valid }"
          name="password"
          vid="password"
          rules="required|min:8"
        >
          <b-form-group
            class="registration-input-group"
            label="Password"
            label-for="password"
            label-class="registration-label"
          >
            <div class="position-relative">
              <b-form-input
                id="password"
                v-model="password"
                class="form-control"
                :type="passwordFieldType"
                placeholder="Password"
                :state="errors.length > 0 && touched ? false : null"
                :class="{ 'is-valid': valid && touched }"
                autocomplete="off"
              />
              <feather-icon
                :icon="passwordToggleIcon"
                class="toggle-password-icon"
                @click="togglePasswordVisibility"
              />
            </div>
            <div
              v-if="errors.length > 0 && touched"
              class="invalid-feedback d-block input-error"
            >
              {{ errors[0] }}
            </div>
          </b-form-group>
        </validation-provider>

        <div class="terms-checkbox-container mb-2">
          <b-form-checkbox
            id="terms-checkbox"
            v-model="termsAccepted"
            name="terms-checkbox"
          >
            Yes, I understand and agree to the Kingpin
            <b-link
              href="https://kingpin.global/terms-and-conditions"
              target="_blank"
              class="cursor-pointer link-style"
              @click="handleTermsLinkClick"
            >
              Terms of Service
            </b-link>
            &
            <b-link
              href="https://kingpin.global/privacy-policy"
              target="_blank"
              class="cursor-pointer link-style"
              @click="handlePrivacyLinkClick"
            >
              Privacy Policy
            </b-link>
          </b-form-checkbox>
        </div>

        <b-button
          class="registration-button"
          type="submit"
          :disabled="invalid || !termsAccepted || isLoading"
        >
          {{ isLoading ? 'Signing up...' : 'Sign up' }}
        </b-button>
      </b-form>
    </validation-observer>
  </div>
</template>

<script>
import {
  BForm, BFormGroup, BFormInput, BButton, BFormCheckbox, BLink,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { useFormFields } from '@/views/apps/authentication-v2/useFormFields'
import analytics from '@core/utils/analytics'
import constants from '@/constants'
import { computed, ref, watch } from '@vue/composition-api'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import { debounce } from 'lodash'
import { registerAction } from '@/views/apps/authentication-v2/child-routes/registerAction'

export default {
  name: 'SetCredentials',
  components: {
    BLink,
    BForm,
    BFormGroup,
    BFormInput,
    BButton,
    BFormCheckbox,
    ValidationProvider,
    ValidationObserver,
    FeatherIcon,
  },
  data() {
    return {
      isLoading: false,
    }
  },
  mounted() {
    analytics.track(constants.TRACKS.ACTIONS.REGISTER_V2.CREATE_ACCOUNT_VIEW)
  },
  setup() {
    const { email, password } = useFormFields()
    const passwordFieldType = ref('password')
    const termsAccepted = ref(false)
    const trackInputFields = debounce((newEmail, newPassword, newTermsAccepted) => {
      analytics.track(constants.TRACKS.ACTIONS.REGISTER_V2.CREATE_ACCOUNT_ENTERS_DATA, {
        formData: {
          email: newEmail,
          password_length: newPassword.length,
          areTermsAccepted: newTermsAccepted,
        },
      })
    }, 1000)
    watch(
      [email, password, termsAccepted],
      ([newEmail, newPassword, newTermsAccepted]) => {
        trackInputFields(newEmail, newPassword, newTermsAccepted)
      },
    )
    const togglePasswordVisibility = () => {
      passwordFieldType.value = passwordFieldType.value === 'password' ? 'text' : 'password'
    }
    const passwordToggleIcon = computed(() => (passwordFieldType.value === 'password' ? 'EyeIcon' : 'EyeOffIcon'))
    return {
      email,
      password,
      passwordFieldType,
      passwordToggleIcon,
      termsAccepted,
      togglePasswordVisibility,
    }
  },
  methods: {
    async onSubmit() {
      this.isLoading = true
      try {
        await registerAction({ router: this.$router })
      } finally {
        this.isLoading = false
      }
    },
    handlePrivacyLinkClick() {
      analytics.track(constants.TRACKS.ACTIONS.REGISTER_V2.CREATE_ACCOUNT_PRIVACY_CLICK)
    },
    handleTermsLinkClick() {
      analytics.track(constants.TRACKS.ACTIONS.REGISTER_V2.CREATE_ACCOUNT_TOS_CLICK)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../common-styles.scss';
.registration-form-title {
  margin-bottom: 12px;
}
.link-style {
  color: #0368A0;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
}
.registration-form-subtitle {
  text-align: center;
  margin-bottom: 40px;
  font-size: 16px;
  line-height: 24px;
  color: #6c757d;
}
.registration-form {
  width: 100%;
}
.registration-input-group {
  margin-bottom: 20px;
}
.toggle-password-icon {
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  cursor: pointer;
  width: 24px;
  height: 16px;
}
.terms-checkbox-container {
  font-size: 16px;
  margin-top: 16px;
  & > .custom-checkbox {
    display: flex;
    flex-direction: row;
    align-items: baseline;
    gap: 7px;
    padding-left: 0;
  }
}
.terms-checkbox-container {
  ::v-deep #terms-checkbox {
    opacity: 1 !important;
    width: 13px;
    height: 13px;
    position: relative;
    z-index: 1;
  }
  ::v-deep #terms-checkbox + .custom-control-label{
    font-size: 16px;
  }
  ::v-deep #terms-checkbox + .custom-control-label::before {
    display: none !important;
  }
}
#email.form-control:focus,
input#password.form-control:focus {
  border-radius: 4.8px;
  border: 1px solid #80BDFF;
  background: #FFF;
  box-shadow: 0 0 0 3.2px rgba(0, 123, 255, 0.25);
}
#email.form-control.is-valid,
#password.form-control.is-valid {
  border-radius: 4.8px;
  border: 1px solid #28A745;
  background: #FFF;
  padding: 8px 40px 8px 16px;
}
.or-divider {
  display: block;
  position: relative;
  text-align: center;
  color: #6c757d;
  &:before, &:after {
    content: "";
    position: absolute;
    top: 50%;
    width: 45%;
    height: 1px;
    background-color: #dee2e6;
  }
  &:before {
    left: 0;
  }
  &:after {
    right: 0;
  }
}
.social-buttons {
  display: flex;
  justify-content: space-between;
  margin-top: 16px;
}
.social-button {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px 16px;
  font-size: 14px;
  .social-icon {
    height: 20px;
    width: 20px;
    margin-right: 8px;
  }
}
#password.form-control.is-invalid {
  background-position: calc(100% - 35px) center;
}
@media screen and (max-width: 660px) {
  .registration-form-subtitle {
    font-size: 14px;
    line-height: 20px;
    margin-bottom: 16px;
  }
  .social-buttons {
    flex-direction: column;
    .social-button {
      margin-right: 0 !important;
      margin-bottom: 8px;
    }
  }
}
</style>
